import { uniqBy } from 'lodash';
import { Invoice } from '../types/finance.types';

export interface InvoiceContact {
  id: number;
  email: string;
  full_name: string;
  should_be_sent_invoices: boolean;
}

const toInvoiceContact = (contact: {
  id: number;
  email: string;
  full_name: string;
  should_be_sent_invoices: boolean;
}): InvoiceContact => ({
  id: contact.id,
  email: contact.email,
  full_name: contact.full_name,
  should_be_sent_invoices: contact.should_be_sent_invoices,
});

const getParentInvoiceContact = (invoice: Invoice): InvoiceContact | undefined => {
  const invoiceClient = invoice.invoice_client;
  if (!invoiceClient) return undefined;

  if (invoiceClient.contact) {
    return toInvoiceContact(invoiceClient.contact);
  }

  const matchingCourseContact = (invoice.courses ?? [])
    .flatMap((course) => course.contacts ?? [])
    .find((contact) => contact.id === invoiceClient.invoiceable_id);

  return matchingCourseContact ? toInvoiceContact(matchingCourseContact) : undefined;
};

const isParentInvoiceClient = (invoice: Invoice): boolean =>
  invoice.invoice_client?.invoiceable_type === 'Contact' || !!invoice.invoice_client?.is_personal;

const dedupeContacts = (contacts: InvoiceContact[]): InvoiceContact[] =>
  uniqBy(contacts.filter((c) => c?.id), 'id');

export function getInvoiceContacts(invoice: Invoice | undefined): InvoiceContact[] {
  if (!invoice) return [];

  if (isParentInvoiceClient(invoice)) {
    const parentContact = getParentInvoiceContact(invoice);
    return parentContact ? [parentContact] : [];
  }

  const invoiceClient = invoice.invoice_client;
  const uniqueCourses = uniqBy(invoice.courses ?? [], 'venue_id');
  const contacts: InvoiceContact[] = [];

  if (invoiceClient?.invoiceable_type === 'Organization' && invoiceClient.invoiceable_id) {
    uniqueCourses.forEach((course) => {
      (course.venue?.organizations ?? [])
        .filter((org) => org.id === invoiceClient.invoiceable_id)
        .forEach((org) => {
          (org.contacts ?? []).forEach((contact) => {
            contacts.push(toInvoiceContact(contact));
          });
        });
    });

    (invoiceClient.organization?.contacts ?? []).forEach((contact) => {
      contacts.push(toInvoiceContact(contact));
    });
  } else if (invoiceClient?.invoiceable_type === 'Venue' && invoiceClient.invoiceable_id) {
    uniqueCourses
      .filter((course) => course.venue?.id === invoiceClient.invoiceable_id)
      .forEach((course) => {
        (course.venue?.contacts ?? []).forEach((contact) => {
          contacts.push(toInvoiceContact(contact));
        });
      });

    (invoiceClient.venue?.contacts ?? []).forEach((contact) => {
      contacts.push(toInvoiceContact(contact));
    });
  } else {
    uniqueCourses.forEach((course) => {
      (course.venue?.contacts ?? []).forEach((contact) => {
        contacts.push(toInvoiceContact(contact));
      });
    });
  }

  return dedupeContacts(contacts);
}

export function getPreselectedInvoiceContactIds(invoice: Invoice | undefined): string[] {
  return getInvoiceContacts(invoice)
    .filter((contact) => contact.should_be_sent_invoices)
    .map((contact) => String(contact.id));
}

export function getInvoiceContactManageLinks(
  invoice: Invoice | undefined
): { label: string; url: string }[] {
  if (!invoice || isParentInvoiceClient(invoice)) return [];

  const invoiceClient = invoice.invoice_client;
  if (!invoiceClient) return [];

  if (invoiceClient.invoiceable_type === 'Organization' && invoiceClient.invoiceable_id) {
    const orgName =
      invoiceClient.organization?.name ??
      invoice.courses
        ?.flatMap((course) => course.venue?.organizations ?? [])
        .find((org) => org.id === invoiceClient.invoiceable_id)?.name ??
      'Organization';

    return [
      {
        label: `Manage ${orgName} Contacts`,
        url: `/admin/places/organizations/${invoiceClient.invoiceable_id}`,
      },
    ];
  }

  if (invoiceClient.invoiceable_type === 'Venue' && invoiceClient.invoiceable_id) {
    const venueTitle =
      invoiceClient.venue?.title ??
      uniqBy(invoice.courses ?? [], 'venue_id').find(
        (course) => course.venue?.id === invoiceClient.invoiceable_id
      )?.venue?.title ??
      'Venue';

    return [
      {
        label: `Manage ${venueTitle} Contacts`,
        url: `/admin/places/venues/${invoiceClient.invoiceable_id}`,
      },
    ];
  }

  return [];
}
